import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import Grid from '@material-ui/core/Grid';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FaEdit } from 'react-icons/fa';
import { FaUserXmark } from 'react-icons/fa6';
import { FiAlertTriangle } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';
import { RiArrowLeftSLine, RiUserAddLine } from 'react-icons/ri';

import { AxiosError } from 'axios';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import { useToast } from '../../../../hooks/toast';

import api from '../../../../services/api';
import getValidationErrors from '../../../../utils/getValidationsErrors';

import { BiSearchAlt } from 'react-icons/bi';
import userImageDefault from '../../../../assets/logo_tech_Prancheta.png';
import { Advice } from '../../../../components/Advice';
import Select from '../../../../components/Select';
import Slider from '../../../../components/Slider';
import Upload from '../../../../components/Upload';
import { IUser, useAuth } from '../../../../hooks/auth';
import { IClient } from '../../../../interfaces/IClient';
import { IRoleList } from '../../../../interfaces/IRole';
import * as S from './styles';

interface ISearchUserParams {
  searchUser?: string;
  client_id?: string | undefined
}

interface INewUser {
  name: string;
  email: string;
  role_id: string;
  client_id: string;
  password: string;
  confirm_password: string;
  default_password: boolean;
}

const AdminUser: React.FC = () => {
  const personalFormRef = useRef<FormHandles>(null);
  const personalUpdateFormRef = useRef<FormHandles>(null);
  const searchFormRef = useRef<FormHandles>(null);
  const { user: authUser } = useAuth();
  const { addToast } = useToast();

  const [loader, setLoader] = useState(false);
  const [userList, setUserList] = useState<IUser[]>([] as IUser[]);
  const [register, setRegister] = useState(false);
  const [updateUser, setUpdateUser] = useState<IUser>();
  const [file, setFile] = useState<File>();
  const [deleteAvatar, setDeleteAvatar] = useState(false);
  const [deleteUser, setDeleteUser] = useState(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState(false);
  const [clientList, setClientList] = useState<IClient[]>([] as IClient[]);
  const [roleList, setRoleList] = useState<IRoleList[]>([] as IRoleList[]);

  const isSuperClient: boolean = authUser.sysadmin || authUser.client_admin;
  const canCreate: boolean = authUser.sysadmin || (authUser.permissions?.includes("|user-create|") || false);
  const canUpdate: boolean =authUser.sysadmin ||(authUser.permissions?.includes("|user-update|") || false);
  const canDelete: boolean = authUser.sysadmin || (authUser.permissions?.includes("|user-delete|") || false);

  
  const getClientList = useCallback(async () => {
    try {
      const { data } = await api.get('clients/tocombo');
      return setClientList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar Clientes',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);

  const getRolesList = useCallback(async (clientId: string) => {
    if (!clientId) {
      setRoleList([]);
      return;
    }
    try {
      const { data } = await api.get(`roles/tocombo/${clientId}`);
      return setRoleList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar Perfil',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);

  const searchHandle = useCallback(async () => {
    const name = searchFormRef.current?.getFieldValue('searchUser');
    let client_id = undefined 
    
    if (isSuperClient) {
      client_id = searchFormRef.current?.getFieldValue('searchClient');
    }
    
    try {
      await searchUsersList({ searchUser: name, client_id });
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar Perfis',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);

  const searchUsersList = useCallback(
    async ({ searchUser, client_id }: ISearchUserParams) => {
      try {
        const { data } = await api.get('profiles', { params: { searchUser, client_id } });

        return setUserList(data);
      } catch (error) {
        return addToast({
          type: 'error',
          title: 'Erro ao buscar usuários',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast],
  );

  useEffect(() => {
    searchUsersList({});
    if (isSuperClient) getClientList();
    if (!isSuperClient) getRolesList(authUser?.client_id)
  }, [searchUsersList, getClientList, getRolesList]);

  const personalFormHandler = useCallback(
    async (formData: INewUser) => {
      setLoader(true);

      try {
        personalFormRef.current?.setErrors({});

        let fieldsToValidate =  {
          email: Yup.string().email().required(),
          name: Yup.string().required(),
          client_id: Yup.string().required(),
          role_id: Yup.string().required(),
          password: Yup.string(),
          confirm_password: Yup.string().equals([formData.password]),
        }

        if (!isSuperClient)
          formData.client_id = authUser.client_id;


        const schema = Yup.object().shape(fieldsToValidate);

        await schema.validate(formData, {
          abortEarly: false,
        });

        const dataToSend = {
          name: formData.name,
          email: formData.email,
          client_id: formData.client_id,
          role_id: formData.role_id,
          password: formData.default_password ? 'SWTech' : formData.password,
          password_confirmation: formData.default_password ? 'SWTech' : formData.password,
        };

        const data = new FormData();

        if (file) {
          data.append('file', file, file.name);
        }

        const user = await api.post('/users', dataToSend);

        await api.patch('profiles/avatar', data, {
          params: {
            userId: user.data.id,
          },
        });

        personalFormRef.current?.reset();

        setRegister(false);
        searchUsersList({});
        setFile(undefined);

        return addToast({
          type: 'success',
          title: 'Usuário criado!',
          description: 'Usuário criado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          return personalFormRef.current?.setErrors(errors);
        }

        return addToast({
          type: 'error',
          title: 'Erro ao criar novo usuário',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, file, searchUsersList],
  );

  const personalUpdateFormHandler = useCallback(
    async formData => {
      setLoader(true);

      try {
        personalUpdateFormRef.current?.setErrors({});

        let fieldsToValidate =  {
          email: Yup.string().email().required('E-mail é obrigatório'),
          name: Yup.string().required('Nome é obrigatório'),
          client_id: Yup.string().required('Cliente é obrigatório'),
          role_id: Yup.string().required('Perfil é obrigatório'),
          password: Yup.string(),
          confirm_password: Yup.string().equals([formData.password]),
        }

        if (!isSuperClient)
          formData.client_id = authUser.client_id;


        const schema = Yup.object().shape(fieldsToValidate);

        await schema.validate(formData, {
          abortEarly: false,
        });

        const dataToSend = {
          name: formData.name,
          email: formData.email,
          client_id: formData.client_id,
          role_id: formData.role_id,
          password: formData.password,
          oldPassword : formData.oldPassword,
          password_confirmation: formData.password,
          user_id: updateUser?.id,
        };

        formData.password === '' && delete dataToSend.password;
        formData.password === '' && delete dataToSend.password_confirmation;
        formData.oldPassword === '' && delete dataToSend.oldPassword;

        const data = new FormData();

        if (file) {
          data.append('file', file, file.name);
        }

        (deleteAvatar || file) &&
          (await api.patch('profiles/avatar', data, {
            params: {
              userId: updateUser?.id,
            },
          }));

        await api.put('/profiles', dataToSend);

        personalFormRef.current?.reset();

        setRegister(false);
        setUpdateUser(undefined);
        setFile(undefined);
        searchUsersList({});

        return addToast({
          type: 'success',
          title: 'Usuário atualizado!',
          description: 'Usuário atualizado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          return personalUpdateFormRef.current?.setErrors(errors);
        }

        return addToast({
          type: 'error',
          title: 'Erro ao atualizar usuário',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, updateUser, searchUsersList, file, deleteAvatar],
  );

  const personalDeleteHandler = useCallback(async () => {
    setLoader(true);

    try {
      const data = new FormData();


      await api.delete('/profiles', {
        data: {
          id: updateUser?.id,
        },
      });

      setUpdateUser(undefined);
      setFile(undefined);
      setDeleteUser(false);
      searchUsersList({});

      return addToast({
        type: 'success',
        title: 'Usuário excluído!',
        description: 'Usuário excluído com sucesso!',
      });
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao excluir usuário',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    } finally {
      setLoader(false);
      setDeleteUser(false);
    }
  }, [addToast, updateUser, searchUsersList]);

  const personalForm = useMemo(
    () => (
      <>
        <Form ref={personalFormRef} onSubmit={personalFormHandler}>
          {(authUser.sysadmin || authUser.permissions?.includes("|users-admin|")) && (
            <div className="upload">
              {!file ? (
                <Upload uploadData={data => setFile(data[0])} />
              ) : (
                <>
                  <HiOutlineTrash
                    size={20}
                    onClick={() => {
                      setFile(undefined);
                    }}
                  />
                  <img src={file ? URL.createObjectURL(file) : ''} alt="logo" />
                </>
              )}
            </div>
          )}
          <div className="form">
            <Grid container spacing={5}>
              <Grid item xs={12} sm={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                    <p>Nome</p>
                    <Input name="name" type="text" />
                  </Grid>
                  <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                    <p>Email</p>
                    <Input name="email" type="email" />
                  </Grid>
                  {isSuperClient && (
                    <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                      <p>Cliente</p>
                      <Select name="client_id" data={clientList} onChange={(event) => getRolesList(event.target.value)} />
                    </Grid>
                  )}
                  <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                      <p>Perfil</p>
                      <Select name="role_id" data={roleList} />
                    </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Grid container spacing={2}>
                  {!useDefaultPassword && (
                    <>
                      <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                        <p>Senha</p>
                        <Input name="password" type="password" />
                      </Grid>
                      <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                        <p>Confirme a senha</p>
                        <Input name="confirm_password" type="password" />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                    <p>Usar senha padrão</p>
                    <Slider
                      name="default_password"
                      defaultChecked={useDefaultPassword}
                      onChange={({ target }) => {
                        setUseDefaultPassword(target.checked);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid>
              {loader ? (
                <Button>Cadastrando ...</Button>
              ) : (
                <Button>
                  <RiUserAddLine size={20} />
                  Cadastrar
                </Button>
              )}
            </Grid>
          </div>
        </Form>
      </>
    ),
    [loader, personalFormHandler, file, useDefaultPassword, clientList, roleList],
  );

  const personalUpdateForm = useMemo(
    () => (
      <>
        <Form ref={personalUpdateFormRef} onSubmit={formData => personalUpdateFormHandler({ ...formData, id: updateUser?.id })}>
          {(authUser.sysadmin || authUser.permissions?.includes("|level-admin|") || authUser.permissions?.includes("|level-manager|")) && (
              <div className="upload">
                {!file && !updateUser?.avatar_url ? (
                  <Upload uploadData={data => setFile(data[0])} />
                ) : (
                  <>
                    <HiOutlineTrash
                      size={20}
                      onClick={() => {
                        setFile(undefined);
                        setDeleteAvatar(true);
                        setUpdateUser(prev => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            avatar_url: '',
                          };
                        });
                      }}
                    />
                    <img
                      src={file ? URL.createObjectURL(file) : (updateUser?.avatar_url || userImageDefault)}
                      alt={updateUser?.name ? `Avatar de ${updateUser.name}` : "Avatar do usuário"}
                      className={file || updateUser?.avatar_url ? "" : "default"}
                      onError={e => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = userImageDefault;
                        e.currentTarget.className = "default";
                      }}
                    />
                  </>
                )}
              </div>
          )}
          <div className="form">
            <Grid container spacing={5}>
              <Grid item xs={12} sm={12} md={6}>
                <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                  <p>Nome</p>
                  <Input name="name" type="text" defaultValue={updateUser?.name} />
                </Grid>
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <p>Email</p>
                  <Input name="email" type="email" defaultValue={updateUser?.email} />
                </Grid>
                {isSuperClient && (
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <p>Cliente</p>
                  {<Select name="client_id" defaultValue={updateUser?.client_id} data={clientList}
                    onChange={(event) => getRolesList(event.target.value)}
                  />}
                </Grid>
                )}
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <p>Perfil</p>
                  <Select name="role_id" data={roleList} defaultValue={updateUser?.role_id}/>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <p>Senha Antiga</p>
                  <Input name="oldPassword" type="password" />
                </Grid>
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <p>Senha Nova</p>
                  <Input name="password" type="password" />
                </Grid>
              </Grid>
            </Grid>
            {canUpdate &&(
              <Grid>
                {loader ? (
                  <Button>Atualizando ...</Button>
                ) : (
                  <Button>
                    <RiUserAddLine size={20} />
                    Atualizar
                  </Button>
                )}
              </Grid>
            )}
            
          </div>
          {canDelete && <HiOutlineTrash size={15} className="trash" onClick={() => setDeleteUser(true)} />}
        </Form>
      </>
    ),
    [loader, personalUpdateFormHandler, updateUser, file, clientList, roleList, canDelete],
  );

  const searchForm = useMemo(() => {
    return (
      <Form onSubmit={searchHandle} ref={searchFormRef} className="search">
        <h3>
          <BiSearchAlt />
          Buscar
          <span> Perfis</span>
        </h3>
        <Input name="searchUser" type="text" placeholder="Nome do Usuário..." />
        {(isSuperClient) && <Select name="searchClient" data={clientList} placeholder="Cliente..." />}
        <Button>pesquisar</Button>
      </Form>
    );
  }, [searchHandle, clientList]);

  return (
    <>
      <S.Container>
        {(register || updateUser) && (
          <div className="close">
            <button
              type="button"
              onClick={() => {
                setRegister(false);
                setUpdateUser(undefined);
                setFile(undefined);
              }}
            >
              <RiArrowLeftSLine size={20} />
              <p>Voltar</p>
            </button>
          </div>
        )}

        {register && <S.AdminUpdateFormContainer>{personalForm}</S.AdminUpdateFormContainer>}

        {updateUser && <S.AdminUpdateFormContainer>{personalUpdateForm}</S.AdminUpdateFormContainer>}

        {!register && !updateUser && (
          <>
          {canCreate && (
            <S.Register>
              <button
                className="register"
                type="button"
                onClick={() => {
                  setRegister(true);
                }}
              >
                <RiUserAddLine size={20} />
                Cadastrar
              </button>
            </S.Register>
          )}
            
            {/* <S.SearchForm>{searchForm}</S.SearchForm> */}
            <S.ListUser>
              {userList.length !== 0 ? (
                <>
                <ul>
                  <li key="header">
                      <div className="userItem">
                      
                        <p className="name">Nome</p>
                        <p className="email">E-mail</p>
                        
                        {isSuperClient &&(
                          <p className="name">Cliente</p>
                        )}
                        
                        <p className="role">Perfil</p>
                      </div>
                    </li>
                </ul>
                <ul>
                  {userList.map(user => (
                    <li key={user.id}>
                      <div className="userItem">
                      
                        {(authUser.sysadmin || authUser.permissions?.includes("|level-admin|") || authUser.permissions?.includes("|level-manager|")) && (
                          <>
                            <img
                              src={user.avatar_url || userImageDefault}
                              alt={user.name ? `Avatar de ${user.name}` : "Avatar do usuário"}
                              className={user.avatar_url ? "" : "default"}
                              onError={e => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = userImageDefault;
                                e.currentTarget.className = "default";
                              }}
                            />
                          </>
                        )}
                        
                        <p className="name">{user.name}</p>
                        <p className="email">{user.email}</p>
                        
                        {isSuperClient &&(
                          <p className="name">{user.client.name}</p>
                        )}
                        
                        <p className="role">{user.role.name}</p>
                        <FaEdit
                          onClick={async () => {
                            await getRolesList(user.client_id);
                            console.log('Edit user avatar_url:', user.avatar_url);
                            setUpdateUser(user);
                          }}
                        />
                      </div>
                      <ul className="operatorList">
                        {user.operations &&
                          user.operations.map(operator => (
                            <li key={operator.id} className="userItem">
                              <p className="name">{operator.name}</p>
                              <p className="email">{operator.email}</p>
                              <p className="role">{operator.role.name}</p>
                              <FaEdit
                                onClick={async () => {
                                  await getRolesList(user.client_id)
                                  setUpdateUser(operator);
                                }}
                              />
                            </li>
                          ))}
                      </ul>
                    </li>
                  ))}
                </ul>
                </>
              ) : (
                <Grid item xs={12}>
                  <Advice text="Nenhum usuário cadastrado" icon={FaUserXmark} />
                </Grid>
              )}
            </S.ListUser>
          </>
        )}
        {deleteUser && (
          <div className="delete">
            <div>
              <FiAlertTriangle size={30} />
              <p>Deseja excluir o usuário?</p>
              <div className="buttons">
                <Button className="c" onClick={() => setDeleteUser(false)}>
                  Cancelar
                </Button>
                <Button className="d" onClick={personalDeleteHandler}>
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        )}
      </S.Container>
    </>
  );
};

export default AdminUser;
